import { useMemo } from "react";

import { SidebarTopResultsWidgetRow } from "@/components/app/app-layout/sidebar-top-resutls-widget/sidebar-top-results-widget-row";
import { CoinCategoryFormatter } from "@/components/shared/coin-category-formatter";
import { EmptyState } from "@/components/shared/empty-state";
import { type DaoCoinStat } from "@/graphql/codegen/graphql";
import { abbreviateNumber } from "@/utils/helpers";

import { TimePeriod } from "../../discover-sort-constants";
import { DiscoverWidgetUserRowSkeleton } from "./discover-widget-user-row-skeleton";
import { WidgetPreset } from "./sidebar-top-results-widget-time-period";
import { useDiscoverWidgetQuery } from "./use-discover-widget-query";

export type CoinViewTimePeriod =
  | TimePeriod.Past24H
  | TimePeriod.Past30D
  | TimePeriod.Past7D
  | "Past1H";

export const TopCoinsWidgetView = ({
  selectedTimePeriod,
}: {
  selectedTimePeriod: CoinViewTimePeriod;
}) => {
  const { initialLoading, items, loadingMarkup } = useDiscoverWidgetQuery({
    preset: WidgetPreset.topCoins,
    timePeriod: selectedTimePeriod,
  });
  const coins = items as Partial<DaoCoinStat>[];

  return (
    <div className="flex w-full flex-col items-start justify-between">
      {!initialLoading && coins.length === 0 && (
        <div className="flex size-full items-center justify-center p-2 py-4">
          <EmptyState
            image="/assets/empty-31.png"
            title="No results."
            description="Try adjusting the filters."
          />
        </div>
      )}
      <div
        className="
          custom-scrollbar custom-scrollbar-hover-only h-[400px] w-full
          overflow-auto
        "
      >
        {coins.map((coin) => (
          <TopCoinWidgetViewRow
            key={coin.coinPkid}
            coin={coin}
            selectedTimePeriod={selectedTimePeriod}
          />
        ))}
        {initialLoading ? (
          <DiscoverWidgetUserRowSkeleton count={10} />
        ) : (
          loadingMarkup
        )}
      </div>
    </div>
  );
};

const TopCoinWidgetViewRow = ({
  coin,
  selectedTimePeriod,
}: {
  coin: Partial<DaoCoinStat>;
  selectedTimePeriod: CoinViewTimePeriod;
}) => {
  const {
    coinPublicKey,
    coinUsername,
    coinCategory,
    volumeUsdPast1H,
    volumeUsdPast24H,
    volumeUsdPast30D,
    volumeUsdPast7D,
  } = coin;

  const finalVolumeValue = useMemo(() => {
    switch (selectedTimePeriod) {
      case "Past1H":
        return volumeUsdPast1H;
      case TimePeriod.Past24H:
        return volumeUsdPast24H;
      case TimePeriod.Past7D:
        return volumeUsdPast7D;
      case TimePeriod.Past30D:
        return volumeUsdPast30D;
      default:
        return 0;
    }
  }, [
    selectedTimePeriod,
    volumeUsdPast1H,
    volumeUsdPast24H,
    volumeUsdPast30D,
    volumeUsdPast7D,
  ]);

  if (!coinPublicKey) {
    return;
  }

  return (
    <SidebarTopResultsWidgetRow
      isCoin
      username={coinUsername}
      publicKey={coinPublicKey}
      renderUsername={<CoinCategoryFormatter coinCategory={coinCategory} />}
      usernamePrefix="$"
    >
      <p
        className="
          font-shadow-green font-mono text-xs font-medium text-emerald-400
        "
      >
        ${abbreviateNumber(finalVolumeValue || 0)}
      </p>
      <p className="text-xs text-muted">Volume</p>
    </SidebarTopResultsWidgetRow>
  );
};
